import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ResponsiveDialog } from '@/components/ResponsiveDialog/ResponsiveDialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGetAllStatesAndTerritoriesQuery } from '@/services/ApiService';
import { useUpdateUserProfile } from '@/hooks/user/useUpdateUserProfile';
import { useUser } from '@/hooks/user/useUser.ts';

const StateSelectionSchema = z.object({
    currentUsStateOrTerritoryId: z.string().min(1, { message: 'State is required' }),
});

export const StateSelectionModal = () => {
    const { user, isFetching } = useUser();
    const missingState = !user?.userProfile?.currentUsStateOrTerritoryId;
    const [isStateModalOpen, setIsStateModalOpen] = useState(false);
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    const { data: allStates = [] } = useGetAllStatesAndTerritoriesQuery();
    const updateUserProfile = useUpdateUserProfile();

    const form = useForm<z.infer<typeof StateSelectionSchema>>({
        resolver: zodResolver(StateSelectionSchema),
        defaultValues: {
            currentUsStateOrTerritoryId: '',
        },
    });

    useEffect(() => {
        if (!isFetching && Boolean(user?.userProfile) && missingState) {
            setIsStateModalOpen(true);
        }
    }, [isFetching, user?.userProfile, missingState]);

    const onSubmit = useCallback(async (data: z.infer<typeof StateSelectionSchema>) => {
        setIsUpdatingProfile(true);
        try {
            await updateUserProfile({
                currentUsStateOrTerritoryId: data.currentUsStateOrTerritoryId,
            });
            setIsStateModalOpen(false);
        }
        finally {
            setIsUpdatingProfile(false);
        }
    }, [updateUserProfile]);

    const preventClose = useCallback(() => {
        // No-op to make modal non-dismissable
    }, []);

    return (
        <ResponsiveDialog
            title="Complete your profile"
            subtitle="Please select your state to continue."
            isOpen={isStateModalOpen}
            onClose={preventClose}
            showCloseButton={false}
            footer={(
                <Button
                    variant="primary"
                    className="w-full"
                    size="lg"
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={!form.formState.isValid || isUpdatingProfile}
                >
                    {isUpdatingProfile ? 'Saving...' : 'Continue'}
                </Button>
            )}
        >
            <Form {...form}>
                <form className="space-y-6 outline-none mb-2">
                    <div data-testid="current-us-state-or-territory-id">
                        <FormField
                            control={form.control}
                            name="currentUsStateOrTerritoryId"
                            render={({ field }) => (
                                <FormItem className="flex flex-col">
                                    <FormLabel className="text-base">
                                        Where do you live?
                                        <span className="text-destructive ml-2">*</span>
                                    </FormLabel>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <FormControl>
                                            <SelectTrigger className={cn('w-full h-13 [&:not([data-placeholder])]:!text-black [&:not([data-placeholder])]:light:!text-black [&:not([data-placeholder])]:dark:!text-foreground [&[data-placeholder]]:!text-gray-dark [&>span]:!text-black [&[data-placeholder]>span]:!text-gray-dark light:border dark:border light:border-gray-dark dark:border-gray-dark dark:focus-visible:border-gz-marine-700', 'bg-white')}>
                                                <SelectValue placeholder="Select a state" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className="max-h-64 overflow-y-auto light bg-white text-foreground">
                                            {allStates.map(option => (
                                                <SelectItem key={option.id} value={option.id} aria-label={`${option.name} (${option.abbreviation})`}>
                                                    {`${option.name} (${option.abbreviation})`}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </form>
            </Form>
        </ResponsiveDialog>
    );
};
